import { useEffect, useState } from 'react';
import { toSafeHttpUrl } from '../common/safeUrl';
import type { CertificateFont } from '../../services/certificateFontService';

/** 에디터 미리보기에서 사용자 폰트에 쓰는 CSS font-family 이름 */
export function getCertificateFontFamily(fontId: number) {
  return `certificate-font-${fontId}`;
}

// 같은 폰트(id + url)는 앱 전체에서 한 번만 내려받는다. 로드에 실패해도 다시 시도하지 않고 기본 폰트로 보인다.
const fontLoadCache = new Map<string, Promise<boolean>>();

function loadFontFace(font: CertificateFont): Promise<boolean> {
  const cacheKey = `${font.id}:${font.fileUrl}`;
  const cached = fontLoadCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const safeUrl = toSafeHttpUrl(font.fileUrl);
  const promise = safeUrl
    ? new FontFace(getCertificateFontFamily(font.id), `url(${JSON.stringify(safeUrl)})`)
        .load()
        .then((fontFace) => {
          document.fonts.add(fontFace);
          return true;
        })
        .catch(() => false)
    : Promise.resolve(false);

  fontLoadCache.set(cacheKey, promise);
  return promise;
}

/**
 * 등록된 폰트 파일을 브라우저에 로드해 캔버스(Konva) 미리보기에서 쓸 수 있게 한다.
 * 반환값은 로드가 끝난 fontId 집합이며, 집합이 바뀌면 컴포넌트가 다시 그려진다.
 * 로드에 실패한 폰트(CORS 등)는 집합에 들어가지 않아 미리보기가 기본 폰트로 보인다.
 */
export function useCertificateFontFaces(fonts: CertificateFont[]) {
  const [loadedFontIds, setLoadedFontIds] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    let ignore = false;

    fonts.forEach((font) => {
      void loadFontFace(font).then((loaded) => {
        if (ignore || !loaded) {
          return;
        }
        setLoadedFontIds((current) => (current.has(font.id) ? current : new Set(current).add(font.id)));
      });
    });

    return () => {
      ignore = true;
    };
  }, [fonts]);

  return loadedFontIds;
}
